import React, { useState } from 'react';
import {
  Box,
  Text,
  Icon,
  Input,
  Textarea,
  Button,
  useToast,
  Spinner,
  Collapse,
  Alert,
  AlertIcon,
  Grid,
  VStack,
  HStack
} from '@chakra-ui/react';
import { Sparkles } from 'lucide-react';
import { UserFeedPreference, userPreferencesApi } from '../../../apis/user-preferences';
import WandStars from '../../../assets/wand-stars-bold.svg';
import CategoryIcon from '../../../assets/category-icon.svg';
import ArrowLeft from '../../../assets/arrow-left-white-icon.svg';
import { AlgorithmPreview } from './AlgorithmPreview';
import { FeedTester } from './FeedTester';
import { PromptValidator } from './PromptValidator';

interface CreatePresetProps {
  userPresets: { data?: UserFeedPreference[] };
  activePreset: { data?: UserFeedPreference };
  getPresetIcon: (preset: UserFeedPreference) => React.ReactElement;
  handlePresetSelect: (preset: UserFeedPreference) => void;
  handleBackToFeed1: () => void;
  onSubmit: (manualWeights?: Record<string, number>) => void;
  description: string;
  setDescription: (value: string) => void;
  presetName: string;
  setPresetName: (value: string) => void;
  manualWeights: Record<string, number>;
  onWeightChange: (weightKey: string, value: number) => void;
  onAiWeightsUpdate: (weights: Record<string, number>) => void;
}

export const CreatePreset: React.FC<CreatePresetProps> = ({
  handleBackToFeed1,
  onSubmit,
  description,
  setDescription,
  presetName,
  setPresetName,
  manualWeights,
  onWeightChange,
  onAiWeightsUpdate
}) => {
  const [isEnriching, setIsEnriching] = useState(false);
  const [originalInstruction, setOriginalInstruction] = useState<string>('');
  const [showComparison, setShowComparison] = useState(false);
  const [showFullOriginal, setShowFullOriginal] = useState(false);
  const toast = useToast();

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleEnrichInstruction = async () => {
    if (!description.trim()) {
      toast({
        title: 'Please enter a description first',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (description.trim().length < 10) {
      toast({
        title: 'Please enter a more detailed description',
        description: 'Your description should be at least 10 characters long.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setIsEnriching(true);
    setOriginalInstruction(description);

    try {
      const result = await userPreferencesApi.enrichInstruction(description);

      if (result.wasEnriched) {
        const cleanedInstruction = result.enrichedInstruction.replace(/^["']|["']$/g, '');
        setDescription(cleanedInstruction);
        setShowComparison(true);
        toast({
          title: '✨ Description enhanced!',
          description:
            'Your description is now optimized for better content matching. You can still edit it further.',
          status: 'success',
          duration: 4000,
          isClosable: true
        });
      } else {
        toast({
          title: '👍 Description looks great!',
          description: 'Your description was already detailed enough for optimal matching.',
          status: 'info',
          duration: 3000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error('Failed to enrich instruction:', error);
      toast({
        title: 'Enhancement failed',
        description: 'Unable to enhance your description. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    } finally {
      setIsEnriching(false);
    }
  };

  const getCharacterCountColor = (count: number) => {
    if (count < 30) return 'red.500';
    if (count < 80) return 'orange.500';
    if (count < 200) return 'green.500';
    return 'blue.500';
  };

  const getCharacterCountLabel = (count: number) => {
    if (count < 30) return 'Too short - consider enhancing';
    if (count < 100) return 'Good length';
    if (count < 200) return 'Great detail!';
    return 'Very detailed!';
  };

  const getTextareaHeight = (text: string) => {
    if (text.length > 300) return '140px';
    if (text.length > 150) return '100px';
    return '80px';
  };


  return (
    <>
      <Box className="flex items-start gap-4 mb-4">
        <div
          className="p-1 md:p-2 bg-growth-green-500 rounded-full transition-transform duration-300 ease-in-out transform hover:scale-105 cursor-pointer flex-shrink-0 mt-1"
          onClick={handleBackToFeed1}
        >
          <img
            src={ArrowLeft}
            alt="Go Back"
            className="w-[17px] h-[17px] md:w-[20px] md:h-[20px]"
          />
        </div>
        <Box className="flex flex-col">
          <Text fontWeight="bold" fontSize={{ base: 'lg', md: 'xl' }} className="flex flex-row items-center gap-1 mb-1">
            Create New Feed Mode
            <img src={CategoryIcon} alt="" className="w-4 h-4" />
          </Text>
          <Text className="text-gray-600" fontSize={{ base: 'sm', md: 'md' }}>
            Create a new custom mode with your own settings
          </Text>
        </Box>
      </Box>
      <Box
        mb={{ base: 4, md: 6 }}
        p={{ base: 3, md: 4 }}
        borderRadius="md"
        borderWidth={1}
        className="bg-growth-green-50"
        borderColor="green.200"
      >
        <Text
          fontSize={{ base: 'sm', md: 'md' }}
          fontWeight={800}
          className="flex flex-row items-center gap-2 text-green-900"
        >
          <img src={WandStars} alt="" className="w-4 h-4" />
          AI-Powered Feed Customization
        </Text>
        <VStack align="start" spacing={1} mt={2}>
          <Text fontSize={{ base: 'xs', md: 'sm' }}>
            • Tell us what kind of content you&apos;re interested about, and we&apos;ll automatically adjust your feed to match your preferences
          </Text>
          <Text fontSize={{ base: 'xs', md: 'sm' }}>
            • After generating your custom feed, you can fine-tune it further using the Advanced settings button and save it as a preset for future use
          </Text>
        </VStack>
      </Box>

      {/* Mode Name Input - moved to top */}
      <Box className="flex flex-col gap-2" mb={{ base: 3, md: 4 }}>
        <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium">Mode Name*</Text>
        <Input
          placeholder="For example: Educational"
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
          size={{ base: 'md', md: 'md' }}
        />
        <Text fontSize={{ base: 'xs', md: 'smaller' }} className="opacity-70">
          If you don&apos;t have a name in mind, it will be generated for you
        </Text>
      </Box>

      <Box className="flex flex-col gap-4">
        <Box className="flex flex-col gap-2">
          <Box className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb={2}">
            <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium">
              Describe the ideal feed you want:
            </Text>
            <Text
              fontSize={{ base: 'xs', md: 'xs' }}
              color={getCharacterCountColor(description.length)}
              fontWeight="medium"
              textAlign={{ base: 'left', sm: 'right' }}
            >
              {description.length} chars • {getCharacterCountLabel(description.length)}
            </Text>
          </Box>
          
          <Text fontSize={{ base: 'xs', md: 'smaller' }} className="opacity-70 mb={2">
            Be as specific as possible for better AI customization
          </Text>

          <VStack spacing={2} align="stretch">
            <Textarea
              placeholder="For example: I want more educational content about technology and science, but keep it lighthearted and fun. I prefer recent articles from trustworthy sources with diverse perspectives."
              height={{ base: '120px', md: getTextareaHeight(description) }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              resize="vertical"
              fontSize={{ base: 'sm', md: 'md' }}
            />
            
            {/* Enhance button - below textarea */}
            {description.trim().length >= 10 && (
              <Box display="flex" justifyContent="flex-end">
                <Button
                  size={{ base: 'sm', md: 'sm' }}
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={isEnriching ? <Spinner size="xs" /> : <Icon as={Sparkles} />}
                  onClick={handleEnrichInstruction}
                  isLoading={isEnriching}
                  loadingText="Enhancing..."
                  isDisabled={!description.trim() || description.trim().length < 10}
                  _hover={{ bg: 'blue.50' }}
                  fontSize={{ base: 'xs', md: 'sm' }}
                >
                  Enhance with AI
                </Button>
              </Box>
            )}
          </VStack>

          <Box className="flex justify-between items-center mt-2">
            <Text fontSize={{ base: 'xs', md: 'smaller' }} className="opacity-70">
              Our AI will analyze your description and automatically adjust the feed algorithm to match your preferences.
            </Text>
          </Box>

          {/* Before/After Comparison - Elegant Design */}
          <Collapse in={showComparison} animateOpacity>
            <Box mt={3} p={3} bg="green.50" borderRadius="md" borderLeft="4px solid" borderColor="green.400">
              <HStack spacing={2} mb={2}>
                <Icon as={Sparkles} color="green.500" boxSize={4} />
                <Text fontSize="sm" fontWeight="medium" color="green.800">
                  Description Enhanced
                </Text>
              </HStack>
              <Text fontSize="xs" color="green.700" mb={1}>
                Original:
              </Text>
              <Text fontSize="xs" color="gray.600" fontStyle="italic" mb={2}>
                "{showFullOriginal ? originalInstruction : truncateText(originalInstruction)}"
              </Text>
              <Text fontSize="xs" color="green.700">
                ✨ Your description is now optimized for better content matching
              </Text>
            </Box>
          </Collapse>

          {/* Prompt Validation */}
          <PromptValidator 
            description={description} 
            isVisible={description.length >= 10}
          />

          {/* Algorithm Preview */}
          <AlgorithmPreview 
            description={description} 
            isVisible={description.length >= 20}
            onWeightChange={onWeightChange}
            manualWeights={manualWeights}
            onAiWeightsUpdate={onAiWeightsUpdate}
          />
        </Box>

      </Box>

      {/* <Box className="mt-2 flex flex-col gap-2">
        <Text fontSize="md">Need more inspiration? Try one of these examples:</Text>
        <Box
          p={4}
          borderRadius="md"
          borderWidth={1}
          className="bg-writer-earth-brown-50 border-03-mid-gray cursor-pointer hover:bg-gray-50 transition-colors"
          borderColor=""
          onClick={() =>
            setDescription(
              'I want more educational content about technology and science, but keep it lighthearted and fun. I prefer recent articles from trustworthy sources with high content quality and original perspectives.'
            )
          }
        >
          <Text fontSize="sm">
            I want more educational content about technology and science, but keep it lighthearted
            and fun. I prefer recent articles from trustworthy sources with high content quality and
            original perspectives.
          </Text>
        </Box>
        <Box
          p={4}
          borderRadius="md"
          borderWidth={1}
          className="bg-writer-earth-brown-50 border-03-mid-gray cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() =>
            setDescription(
              'Show me diverse perspectives on current events with high-quality analysis. I care about factual accuracy, civil discourse, and want to avoid clickbait. Focus on integrity and educational value over entertainment.'
            )
          }
        >
          <Text fontSize="sm">
            Show me diverse perspectives on current events with high-quality analysis. I care about
            factual accuracy, civil discourse, and want to avoid clickbait. Focus on integrity and
            educational value over entertainment.
          </Text>
        </Box>

        <FeedTester 
          description={description} 
          presetName={presetName}
        />
      </Box> */}
    </>
  );
};
